// Library Methods
import { useTranslation } from "react-i18next";
import React, { useState } from "react";

// Components
import { Box, Divider, Grid, Typography, TextField } from "@mui/material";
import ExamCardButton from "../ui/ExamCardButton";
import ExamCardToggles from "../ui/ExamCardToggles";
import TitleWithTooltip from "../../../../../components/UI/TitleWithTooltip";
import ExamCardContainer from "../ui/ExamCardContainer";
import ExamCardHeader from "../ui/ExamCardHeader";

// Utilities
import {
  FixationLossMethodOptions,
  FixationPointShapeOptions,
} from "../../../../../utils/examReportsHelper";
import {
  CustomTextFieldSolid,
  CustomTextFieldOutlinedInputProps,
} from "../../../../../styles/muiStylesHelper";
import {
  DEFAULT_DECIBEL_VALUE_ESTERMAN,
  GridTypes,
  MAX_DECIBEL_VALUE,
  MIN_DECIBEL_VALUE,
} from "../../../../../enums";

const EstermanCard = ({
  label,
  examAlgorithm,
  selectedExamCards,
  updateExamCardProperty,
  onToggleCard,
}) => {
  // internationalization
  const { t } = useTranslation();

  const defaultEsterman = {
    examFieldType: examAlgorithm,
    examType: "ESTERMAN_FAST",
    decibelValue: DEFAULT_DECIBEL_VALUE_ESTERMAN,
    eyeCheckboxes: { right: true, left: true },
    visualGrid: GridTypes.G120,
    fixationType: {
      fixationPointShapeType: FixationPointShapeOptions.Sphere,
      fixationLossMethod: FixationLossMethodOptions.EyeTracking,
    },
    binocular: true,
    optionalToggles: {
      autoPause: true,
      calibration: true,
      encouragement: true,
      requiresTutorial: true,
    },
  };

  const selectedExamCard = selectedExamCards.find(
    (examCard) => examCard.examFieldType === examAlgorithm
  );
  const examCardInfo = selectedExamCard ?? defaultEsterman;
  const isActive = Boolean(selectedExamCard);

  const {
    decibelValue: initialDecibelValue,
    optionalToggles,
    fixationType,
  } = examCardInfo;

  const [decibelValue, setDecibelValue] = useState(initialDecibelValue);

  const textStyle = {
    color: isActive ? "#FFF" : "#5B6D7A",
    fontSize: "14px",
  };

  return (
    <ExamCardContainer isActive={isActive}>
      <ExamCardHeader
        label={label}
        isActive={isActive}
        onChange={(e) => {
          onToggleCard(e, defaultEsterman);
        }}
      />
      <Grid item container display="flex" flexDirection="column" gap="8px">
        {/* esterman type */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("exams_table_type")}
            tooltip={t("more_available_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />
          <ExamCardButton
            isSelected
            updateValue={() =>
              updateExamCardProperty("examType", "ESTERMAN_FAST")
            }
            label={t("word_fast")}
            isDisabled={!isActive}
          />
        </Grid>

        {/* Stimulus Intensity */}
        <Grid item display="flex" flexDirection="column" xs gap="8px">
          <TitleWithTooltip
            title={t("report_stimulus_intensity")}
            tooltip={t("stimulus_intensity_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <TextField
              type="number"
              id="stimulus intensity input"
              disabled={!isActive}
              value={decibelValue}
              onChange={(event) => {
                const newDecibalValue = Number(event?.target?.value);
                setDecibelValue(newDecibalValue);
              }}
              onBlur={(event) => {
                const newDecibalValue = Number(event?.target?.value);
                if (newDecibalValue > MAX_DECIBEL_VALUE) {
                  setDecibelValue(MAX_DECIBEL_VALUE);
                  updateExamCardProperty("decibelValue", MAX_DECIBEL_VALUE);
                } else if (newDecibalValue < MIN_DECIBEL_VALUE) {
                  setDecibelValue(MIN_DECIBEL_VALUE);
                  updateExamCardProperty("decibelValue", MIN_DECIBEL_VALUE);
                } else {
                  setDecibelValue(newDecibalValue);
                  updateExamCardProperty("decibelValue", newDecibalValue);
                }
              }}
              onKeyPress={(event) => {
                const isNotNumber =
                  event?.key === "-" ||
                  event?.key === "+" ||
                  event?.key === ".";
                if (isNotNumber) return;

                const newDecibalValue = Number(event?.target?.value);
                setDecibelValue(newDecibalValue);
                updateExamCardProperty("decibelValue", newDecibalValue);
              }}
              fullWidth
              sx={{ ...CustomTextFieldSolid() }}
              InputProps={{
                inputProps: {
                  min: MIN_DECIBEL_VALUE,
                  max: MAX_DECIBEL_VALUE,
                },
                sx: {
                  ...CustomTextFieldOutlinedInputProps,
                },
              }}
            />
          </Grid>
        </Grid>

        {/* eye */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("word_eye")}
            tooltip={t("which_eye_binocular_test_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />
          <ExamCardButton
            isSelected
            updateValue={() => updateExamCardProperty("binocular", true)}
            label={t("word_binocular")}
            isDisabled={!isActive}
          />
        </Grid>

        {/* Fixation */}
        <Grid item display="flex" flexDirection="column" xs gap="8px">
          <TitleWithTooltip
            title={t("fixation")}
            tooltip={t("fixation_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <Grid item xs>
              <ExamCardButton
                isSelected={
                  FixationPointShapeOptions.Sphere ===
                  fixationType.fixationPointShapeType
                }
                updateValue={() =>
                  updateExamCardProperty("fixationType", {
                    ...examCardInfo.fixationType,
                    fixationPointShapeType: FixationPointShapeOptions.Sphere,
                  })
                }
                label={t("exams_modal_sphere")}
                isDisabled={!isActive}
              />
            </Grid>
            <Grid item xs>
              <ExamCardButton
                isSelected={
                  FixationPointShapeOptions.Diamond ===
                  fixationType.fixationPointShapeType
                }
                updateValue={() =>
                  updateExamCardProperty("fixationType", {
                    ...examCardInfo.fixationType,
                    fixationPointShapeType: FixationPointShapeOptions.Diamond,
                  })
                }
                label={t("diamond")}
                isDisabled={!isActive}
              />
            </Grid>
          </Grid>

          {/* Fixation Loss */}
          <Grid item display="flex" flexDirection="column" xs gap="8px">
            <TitleWithTooltip
              title={t("fixation_loss_method")}
              tooltip={t("fixation_loss_method_tooltip")}
              titleStyle={textStyle}
              tooltipStyle={textStyle}
            />

            <Grid container item display="flex" gap="8px" height="32px">
              <Grid item xs>
                <ExamCardButton
                  isSelected={
                    FixationLossMethodOptions.EyeTracking ===
                    fixationType.fixationLossMethod
                  }
                  updateValue={() =>
                    updateExamCardProperty("fixationType", {
                      ...examCardInfo.fixationType,
                      fixationLossMethod: FixationLossMethodOptions.EyeTracking,
                    })
                  }
                  label={t("eye_tracking")}
                  isDisabled={!isActive}
                />
              </Grid>
              <Grid item xs>
                <ExamCardButton
                  isSelected={
                    FixationLossMethodOptions.BlindSpot ===
                    fixationType.fixationLossMethod
                  }
                  updateValue={() =>
                    updateExamCardProperty("fixationType", {
                      ...examCardInfo.fixationType,
                      fixationLossMethod: FixationLossMethodOptions.BlindSpot,
                    })
                  }
                  label={t("word_blind_spot")}
                  isDisabled={!isActive}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Box height={{ xs: "0px", sm: "210.5px" }} />

        <Divider color="#5B6D7A" />

        <Grid display="flex" flexDirection="column">
          <ExamCardToggles
            isActive={isActive}
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={defaultEsterman.optionalToggles}
          />
        </Grid>

        <Grid item textAlign="center">
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("new_test")}
          </Typography>
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("try_it_out")}
          </Typography>
        </Grid>
      </Grid>
    </ExamCardContainer>
  );
};

export default EstermanCard;
